import cx from 'classnames';
import {
  getCountryOptions,
  getCustomCategoryValueOptions,
  getDepartmentOptions,
  getEmployeeTypeOptions,
  getStateOptions,
} from 'components/admin/datasets';
import { Form, ISelectOption } from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { MinusCircle } from 'ethena-icons';
import { COUNTRY_BY_CODE } from 'lib/datasets/countries';
import {
  prettyPrintCustomCategoryLabel,
  prettyPrintUserRole,
} from 'lib/helpers/prettyPrint';
import { ILocation, UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { getRuleLabelName, isLocationUsa } from './helpers';
import css from './PopulationRuleRow.module.scss';
import { PopulationRuleType } from './types';

interface IPopulationRuleRowProps {
  availableRuleTypes: PopulationRuleType[];
  children: ReactNode;
  combinationOperator: 'AND' | 'OR';
  excludeValue?: boolean;
  isDisabled?: boolean;
  isExcludeToggleDisabled?: boolean;
  onExcludeToggle?: () => void;
  onRemove: (ruleType: PopulationRuleType) => void;
  onReplace: (ruleType: PopulationRuleType) => void;
  onCombinationOperatorToggle: () => void;
  ruleType: PopulationRuleType;
  showCombinationOperator: boolean;
}

export const PopulationRuleRow = ({
  availableRuleTypes,
  children,
  combinationOperator,
  excludeValue = false,
  isDisabled = false,
  isExcludeToggleDisabled = false,
  onExcludeToggle = undefined,
  onRemove,
  onReplace,
  onCombinationOperatorToggle,
  ruleType,
  showCombinationOperator,
}: IPopulationRuleRowProps) => {
  const { session } = useAuthContext();
  const [isCombinationOperatorHovered, setIsCombinationOperatorHovered] =
    useState<boolean>(false);

  const ruleTypeOptions = _.map(PopulationRuleType, (ruleType) => {
    switch (ruleType) {
      case PopulationRuleType.CUSTOM_CATEGORY:
        return {
          label: prettyPrintCustomCategoryLabel(
            session.company.customCategoryLabel
          ),
          value: ruleType,
        };
      default:
        return {
          label: getRuleLabelName(ruleType),
          value: ruleType,
        };
    }
  });

  const handleCombinationOperatorMouseEnter = () => {
    setIsCombinationOperatorHovered(true);
  };

  const handleCombinationOperatorMouseLeave = () => {
    setIsCombinationOperatorHovered(false);
  };

  const renderExcludeToggle = () => (
    <button
      className={css.toggleButton}
      disabled={isDisabled || isExcludeToggleDisabled}
      onClick={onExcludeToggle}
    >
      {excludeValue ? 'is not' : 'is'}
    </button>
  );

  const renderCombinationOperator = () => {
    if (!showCombinationOperator) {
      return null;
    }
    return (
      <div className={css.operatorRow}>
        <button
          className={css.toggleButton}
          disabled={isDisabled}
          onClick={onCombinationOperatorToggle}
          onMouseEnter={handleCombinationOperatorMouseEnter}
          onMouseLeave={handleCombinationOperatorMouseLeave}
        >
          {_.lowerCase(combinationOperator)}
        </button>
        <span>
          {isCombinationOperatorHovered &&
            (combinationOperator === 'AND'
              ? 'Use AND to find members that satisfy all your specified conditions.'
              : 'Use OR to find members that meet any of your specified conditions.')}
        </span>
      </div>
    );
  };

  return (
    <Fragment key={ruleType}>
      {renderCombinationOperator()}
      <div className={css.ruleRow}>
        <Form.Select
          className={css.ruleSelector}
          isDisabled={isDisabled}
          menuPlacement="bottom"
          onChange={({ value }) => onReplace(value as PopulationRuleType)}
          options={ruleTypeOptions}
          filterOption={({ value }) => _.includes(availableRuleTypes, value)}
          value={_.find(ruleTypeOptions, { value: ruleType })}
        />
        {renderExcludeToggle()}
        {children}
        <button
          className={css.removeButton}
          disabled={isDisabled}
          onClick={() => onRemove(ruleType)}
        >
          <MinusCircle className={css.removeIcon} />
        </button>
      </div>
    </Fragment>
  );
};

interface ICustomCategoryRuleRowProps
  extends Omit<
    IPopulationRuleRowProps,
    'children' | 'excludeValue' | 'onExcludeToggle'
  > {
  customCategoryValues?: string[];
  onChange: ({
    customCategoryValues,
    exclude,
  }: {
    customCategoryValues: string[];
    exclude: boolean;
  }) => void;
  value: { customCategoryValues: string[]; exclude?: boolean };
}

export const CustomCategoryRuleRow = ({
  customCategoryValues,
  isDisabled,
  onChange,
  value,
  ...rest
}: ICustomCategoryRuleRowProps) => {
  const { session } = useAuthContext();

  const customCategoryOptions = useMemo(
    () =>
      getCustomCategoryValueOptions(
        session.company.customCategoryLabel,
        customCategoryValues
      ),
    []
  );

  const handleCustomCategoryChange = (
    selectedOptions: ISelectOption<string>[]
  ) => {
    onChange({
      customCategoryValues: _.map(selectedOptions, (v) => v.value),
      exclude: value.exclude,
    });
  };

  const handleExcludeToggle = () => {
    onChange({
      customCategoryValues: value.customCategoryValues,
      exclude: !value.exclude,
    });
  };

  return (
    <PopulationRuleRow
      {...rest}
      isDisabled={isDisabled}
      excludeValue={value.exclude}
      onExcludeToggle={handleExcludeToggle}
    >
      <Form.CheckboxMultiSelect
        includeAll={false}
        isDisabled={isDisabled}
        menuPlacement="bottom"
        multiValueLabel="Values"
        onChange={handleCustomCategoryChange}
        options={customCategoryOptions}
        value={_.filter(customCategoryOptions, (v) =>
          _.includes(value.customCategoryValues, v.value)
        )}
      />
    </PopulationRuleRow>
  );
};

interface IDepartmentRuleRowProps
  extends Omit<
    IPopulationRuleRowProps,
    'children' | 'excludeValue' | 'onExcludeToggle'
  > {
  departments?: string[];
  onChange: ({
    departments,
    exclude,
  }: {
    departments: string[];
    exclude: boolean;
  }) => void;
  value: { departments: string[]; exclude?: boolean };
}

export const DepartmentRuleRow = ({
  departments,
  isDisabled,
  onChange,
  value,
  ...rest
}: IDepartmentRuleRowProps) => {
  const departmentOptions = useMemo(
    () => getDepartmentOptions(departments),
    []
  );

  const handleDepartmentsChange = (
    selectedOptions: ISelectOption<string>[]
  ) => {
    onChange({
      departments: _.map(selectedOptions, (d) => d.value),
      exclude: value.exclude,
    });
  };

  const handleExcludeToggle = () => {
    onChange({
      departments: value.departments,
      exclude: !value.exclude,
    });
  };

  return (
    <PopulationRuleRow
      {...rest}
      isDisabled={isDisabled}
      excludeValue={value.exclude}
      onExcludeToggle={handleExcludeToggle}
    >
      <Form.CheckboxMultiSelect
        includeAll={false}
        isDisabled={isDisabled}
        menuPlacement="bottom"
        multiValueLabel="Departments"
        onChange={handleDepartmentsChange}
        options={departmentOptions}
        value={_.filter(departmentOptions, (d) =>
          _.includes(value.departments, d.value)
        )}
      />
    </PopulationRuleRow>
  );
};

interface IEmployeeTypeRuleRowProps
  extends Omit<
    IPopulationRuleRowProps,
    'children' | 'excludeValue' | 'onExcludeToggle'
  > {
  employeeTypes?: string[];
  onChange: ({
    employeeTypes,
    exclude,
  }: {
    employeeTypes: string[];
    exclude?: boolean;
  }) => void;
  value: { employeeTypes: string[]; exclude?: boolean };
}

export const EmployeeTypeRuleRow = ({
  employeeTypes,
  isDisabled,
  onChange,
  value,
  ...rest
}: IEmployeeTypeRuleRowProps) => {
  const employeeTypeOptions = useMemo(
    () => getEmployeeTypeOptions(employeeTypes),
    []
  );

  const handleEmployeeTypesChange = (
    selectedOptions: ISelectOption<string>[]
  ) => {
    onChange({
      employeeTypes: _.map(selectedOptions, (e) => e.value),
      exclude: value.exclude,
    });
  };

  const handleExcludeToggle = () => {
    onChange({
      employeeTypes: value.employeeTypes,
      exclude: !value.exclude,
    });
  };

  return (
    <PopulationRuleRow
      {...rest}
      isDisabled={isDisabled}
      excludeValue={value.exclude}
      onExcludeToggle={handleExcludeToggle}
    >
      <Form.CheckboxMultiSelect
        includeAll={false}
        isDisabled={isDisabled}
        menuPlacement="bottom"
        multiValueLabel="Worker Types"
        onChange={handleEmployeeTypesChange}
        options={employeeTypeOptions}
        value={_.filter(employeeTypeOptions, (e) =>
          _.includes(value.employeeTypes, e.value)
        )}
      />
    </PopulationRuleRow>
  );
};

interface IRoleRuleRowProps
  extends Omit<
    IPopulationRuleRowProps,
    'children' | 'excludeValue' | 'onExcludeToggle'
  > {
  onChange: (role: UserProfileRole) => void;
  value: UserProfileRole;
}

export const RoleRuleRow = ({
  isDisabled,
  onChange,
  value,
  ...rest
}: IRoleRuleRowProps) => {
  const roleOptions = _.map(UserProfileRole, (role) => ({
    label: prettyPrintUserRole(role),
    value: role,
  }));

  const handleRoleChange = (selectedOption: ISelectOption<UserProfileRole>) => {
    onChange(selectedOption.value);
  };

  return (
    <PopulationRuleRow
      {...rest}
      isDisabled={isDisabled}
      isExcludeToggleDisabled
    >
      <Form.Select
        isDisabled={isDisabled}
        menuPlacement="bottom"
        onChange={handleRoleChange}
        options={roleOptions}
        value={_.find(roleOptions, { value })}
      />
    </PopulationRuleRow>
  );
};

interface ILocationRuleRowProps
  extends Omit<
    IPopulationRuleRowProps,
    'children' | 'excludeValue' | 'onExcludeToggle'
  > {
  locations?: ILocation[];
  onChange: ({
    locations,
    exclude,
  }: {
    locations: ILocation[];
    exclude: boolean;
  }) => void;
  value: { locations: ILocation[]; exclude?: boolean };
}

export const LocationRuleRow = ({
  locations,
  isDisabled,
  onChange,
  value,
  ...rest
}: ILocationRuleRowProps) => {
  const handleExcludeToggle = () => {
    onChange({
      locations: value.locations,
      exclude: !value.exclude,
    });
  };

  return (
    <PopulationRuleRow
      isDisabled={isDisabled}
      excludeValue={value.exclude}
      onExcludeToggle={handleExcludeToggle}
      {...rest}
    >
      <LocationSelector
        isDisabled={isDisabled}
        locations={locations}
        menuPlacement="bottom"
        onChange={onChange}
        value={value}
      />
    </PopulationRuleRow>
  );
};

interface ILocationSelectorProps {
  className?: string;
  isDisabled?: boolean;
  locations?: ILocation[];
  onChange: ({
    locations,
    exclude,
  }: {
    locations: ILocation[];
    exclude: boolean;
  }) => void;
  value: { locations: ILocation[]; exclude?: boolean };
  isInvalid?: boolean;
  menuPlacement?: 'auto' | 'top' | 'bottom';
}

export const LocationSelector = ({
  className,
  isDisabled,
  locations,
  onChange,
  menuPlacement = 'auto',
  value,
}: ILocationSelectorProps) => {
  const isUsaSelected = _.some(value.locations, isLocationUsa);

  const initialLocations = useMemo(
    () =>
      _.isEmpty(locations)
        ? []
        : _.chain(locations)
            .concat(value.locations)
            .uniqBy((l) => l.country + l.state)
            .value(),
    [locations]
  );

  const countryOptions: ISelectOption<ILocation>[] = useMemo(
    () => getCountryOptions(initialLocations),
    [JSON.stringify(initialLocations)]
  );

  const stateOptions: ISelectOption<ILocation>[] = useMemo(
    () => getStateOptions(initialLocations),
    [JSON.stringify(initialLocations)]
  );

  const handleCountriesChange = (selected: ISelectOption<ILocation>[]) => {
    const countries = _.map(selected, (c) => c.value);
    const isUsaSelected = _.some(countries, isLocationUsa);
    const states = _.reject(value.locations, (l) => !l.state);
    // If states are selected, we don't want to include the USA (country).
    const locations =
      isUsaSelected && !_.isEmpty(states)
        ? [...states, ..._.reject(countries, isLocationUsa)]
        : countries;
    onChange({
      locations,
      exclude: value.exclude,
    });
  };

  const handleStatesChange = (selected: ISelectOption<ILocation>[]) => {
    const states = _.map(selected, (s) => s.value);
    const countries = _.filter(value.locations, (l) => !l.state);
    // If no states are selected, we want to include the USA (country).
    const locations = _.isEmpty(states)
      ? [...countries, { country: COUNTRY_BY_CODE.USA.code }]
      : [...states, ..._.reject(countries, isLocationUsa)];
    onChange({
      locations,
      exclude: value.exclude,
    });
  };
  return (
    <div
      className={cx(css.locationCol, className, {
        [css.isStateSelectorVisible]: isUsaSelected,
      })}
    >
      <Form.CheckboxMultiSelect
        // Since `option.value` is an object, we need to translate each option to a string value so react-select can compare the options when determining which options are selected.
        getOptionValue={(option: ISelectOption) => option.label}
        includeAll={false}
        isDisabled={isDisabled}
        isFluidWidth
        menuPlacement={menuPlacement}
        multiValueLabel="Countries"
        onChange={handleCountriesChange}
        options={countryOptions}
        placeholder="Select Countries"
        value={getCountryOptions(value.locations)}
      />
      {isUsaSelected && (
        <Form.CheckboxMultiSelect
          getOptionValue={(option: ISelectOption) => option.label}
          isDisabled={isDisabled}
          isFluidWidth
          menuPlacement={menuPlacement}
          multiValueLabel="States"
          onChange={handleStatesChange}
          options={stateOptions}
          placeholder="All States"
          value={getStateOptions(value.locations)}
        />
      )}
    </div>
  );
};
